import { el, setText, setStyle, setClass, damp, mmss } from './util.js';

function statRow(parent, label) {
  const r = el('div', 'ow-score-row', parent);
  el('div', 'name', r, label);
  return el('div', 'val', r, '0');
}

/**
 * Scoreboard overlay. Two independent visibility drivers:
 *  - hold-Tab while `phase === 'playing'` (`ctx.input.action('scoreboard')`),
 *    fast fade, non-interactive.
 *  - pinned open while `phase === 'over'`, with a scrim + REDEPLOY button.
 *
 * Defensive against a missing `game` subsystem (capture harness): with no
 * GameSystem, phase is treated as 'playing' and the Tab action is simply
 * never held, so the board renders fully hidden — today's behaviour.
 */
export class Scoreboard {
  constructor(parent, ctx) {
    this.ctx = ctx;
    this.root = el('div', 'ow-score', parent);
    const inner = el('div', 'ow-score-inner', this.root);

    const head = el('div', 'ow-score-head', inner);
    this.modeLbl = el('div', 'ow-score-mode', head, 'TDM — MARKET DISTRICT');
    this.clock = el('div', 'ow-score-clock', head, '0:00');
    el('div', 'ow-score-rule', inner);

    this.rows = el('div', null, inner);
    this.killsVal = statRow(this.rows, 'KILLS');
    this.deathsVal = statRow(this.rows, 'DEATHS');
    this.headshotsVal = statRow(this.rows, 'HEADSHOTS');
    this.bestStreakVal = statRow(this.rows, 'BEST STREAK');

    const total = el('div', 'ow-score-total', inner);
    el('div', 'ow-score-total-lbl', total, 'SCORE');
    this.scoreVal = el('div', 'ow-score-total-val', total, '0');

    this.footer = el('div', 'ow-score-footer', inner);
    this.redeployBtn = el('button', 'ow-btn primary', this.footer, 'REDEPLOY');
    this.redeployBtn.type = 'button';
    this.redeployBtn.addEventListener('click', () => {
      this.ctx.peek('game')?.restart?.();
    });
    el('div', 'ow-score-hint', this.footer, 'PRESS REDEPLOY TO RESTART');

    this.shown = 0;
    setStyle(this.root, 'display', 'none');
    setStyle(this.root, 'pointer-events', 'none');
  }

  update(rawDt) {
    const ctx = this.ctx;
    const game = ctx.peek('game');
    const phase = game?.phase ?? 'playing';
    const over = phase === 'over';
    const held = phase === 'playing' && !!ctx.input?.action?.('scoreboard');

    const target = over || held ? 1 : 0;
    this.shown = damp(this.shown, target, over ? 14 : 20, rawDt);

    setClass(this.root, 'over', over);
    setStyle(this.footer, 'display', over ? '' : 'none');

    const stats = game?.stats ?? null;
    setText(this.modeLbl, over ? 'MATCH COMPLETE' : 'TDM — MARKET DISTRICT');
    setText(this.clock, mmss(stats?.timeLeft ?? 0));
    setText(this.killsVal, stats?.kills ?? 0);
    setText(this.deathsVal, stats?.deaths ?? 0);
    setText(this.headshotsVal, stats?.headshots ?? 0);
    setText(this.bestStreakVal, stats?.bestStreak ?? 0);
    const score = (stats?.kills ?? 0) * 100 + (stats?.headshots ?? 0) * 50;
    setText(this.scoreVal, score);

    if (this.shown < 0.004) {
      setStyle(this.root, 'display', 'none');
      setStyle(this.root, 'pointer-events', 'none');
      return;
    }
    setStyle(this.root, 'display', '');
    setStyle(this.root, 'pointer-events', over ? 'auto' : 'none');
    setStyle(this.root, 'opacity', this.shown.toFixed(3));
  }

  dispose() {
    this.root.remove();
  }
}
